import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Book as BookIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { GoBook } from 'react-icons/go';
import {
  createSubject,
  deleteSubject,
  getSubjectsList,
  updateSubject,
} from '../../features/subjects/subjectsSlice';
import { useDispatch } from 'react-redux';
import ConfirmationDialog from '../UIElements/ConfirmationDialog';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import ToastNotification from '../UIElements/ToastNotification';
import useNotification from '../../hooks/useNotification';

const Subjects = () => {
  const dispatch = useDispatch();
  const {
    dialog,
    showDeleteConfirmation,
    closeDialog: closeConfirmDialog,
  } = useConfirmationDialog();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newSubject, setNewSubject] = useState({
    subjectName: '',
  });

  const [editingSubject, setEditingSubject] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const data = await dispatch(getSubjectsList()).unwrap();
      setSubjects(data);
    } catch (error) {
      showError('Failed to fetch subjects. Please try again.');
      console.error('Error fetching subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubject = async () => {
    if (!newSubject.subjectName.trim()) {
      showError('Please enter a subject name');
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        createSubject({
          subjectName: newSubject.subjectName.trim(),
        }),
      ).unwrap();
      // Refresh the subjects list
      await fetchSubjects();
      setNewSubject({ subjectName: '' });
      setOpenDialog(false);
      showSuccess('Subject added successfully!');
    } catch (error) {
      if (error == 'duplicate') {
        showError(' Subject already exists.');
      } else {
        showError(error, 'Failed to add subject. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
    setNewSubject({
      subjectName: subject.subjectName || subject.name || '', // Handle different property names
    });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  const handleUpdateSubject = async () => {
    if (!newSubject.subjectName.trim()) {
      showError('Please enter a subject name');
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        updateSubject({
          id: editingSubject.id,
          subjectName: newSubject.subjectName.trim(),
        }),
      ).unwrap();
      // Refresh the subjects list
      await fetchSubjects();
      setNewSubject({ subjectName: '' });
      setEditingSubject(null);
      setOpenDialog(false);
      showSuccess('Subject updated successfully!');
    } catch (error) {
      if (error == 'duplicate') {
        showError('Duplicate subjects are not allowed.');
      } else {
        showError('Failed to update subject. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubject = async (id) => {
    // Find the subject name for display in confirmation
    const subject = subjects.find((s) => s.id === id);
    const subjectName = subject?.subjectName || subject?.name || 'this subject';

    showDeleteConfirmation(subjectName, () => confirmDeleteSubject(id));
  };

  const confirmDeleteSubject = async (id) => {
    try {
      setLoading(true);
      await dispatch(deleteSubject(id)).unwrap();
      // Refresh the subjects list
      await fetchSubjects();
      showSuccess('Subject deleted successfully!');
      closeConfirmDialog();
    } catch (error) {
      if (error == 'subject mapped') {
        showError("Subject mapped to Exam,so it can't be deleted.");
      } else {
        showError('Failed to delete subject. Please try again.');
      }
      console.error('Error deleting subject:', error);
      closeConfirmDialog();
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setNewSubject({ subjectName: '' });
    setEditingSubject(null);
    setDialogMode('add');
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setNewSubject({ subjectName: '' });
    setEditingSubject(null);
  };

  const totalSubjects = subjects.length;

  return (
    <div className='space-y-6'>
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Header with Statistics */}
      <Card>
        <CardContent>
          <Box className='flex items-center justify-between mb-4 flex-wrap gap-2'>
            <Box className='flex items-center gap-2 flex-wrap'>
              <Box className='size-8 bg-blue-500 rounded-lg flex items-center justify-center'>
                <GoBook className='text-white size-5' />
              </Box>
              <Typography variant='h6' className='text-blue-700 font-semibold'>
                Subject Management
              </Typography>
            </Box>
            <Button
              variant='contained'
              color='primary'
              startIcon={<AddIcon />}
              onClick={openAddDialog}
              size='small'
              disabled={loading}
            >
              Add New Subject
            </Button>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{ backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}
              >
                <CardContent className='text-center py-3'>
                  <Typography variant='h4' className='font-bold text-blue-600'>
                    {totalSubjects}
                  </Typography>
                  <Typography variant='body2' className='text-blue-700'>
                    Total Subjects
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subjects Table */}
      <Card>
        <CardContent>
          <Typography variant='h6' className='mb-4 text-blue-700 font-semibold'>
            Existing Subjects
          </Typography>

          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ maxHeight: '60vh', overflow: 'auto' }}
          >
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>
                    <strong>S.No</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Subject Name</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects.map((subject, index) => (
                  <TableRow
                    key={subject.id}
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box className='flex items-center gap-2'>
                        <BookIcon fontSize='small' className='text-blue-500' />
                        <Typography variant='body2' className='font-medium'>
                          {subject.subjectName || subject.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      <Box className='flex justify-center gap-1'>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() => handleEditSubject(subject)}
                          disabled={loading}
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => handleDeleteSubject(subject.id)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {subjects.length === 0 && !loading && (
            <Box className='text-center py-8'>
              <BookIcon
                className='text-gray-300 mb-2'
                style={{ fontSize: 48 }}
              />
              <Typography variant='h6' className='text-gray-400 mb-2'>
                No Subjects Found
              </Typography>
              <Typography variant='body2' className='text-gray-500'>
                Click "Add New Subject" to create your first subject
              </Typography>
            </Box>
          )}

          {loading && subjects.length === 0 && (
            <Box className='text-center py-8'>
              <CircularProgress />
              <Typography variant='body2' className='text-gray-500 mt-2'>
                Loading subjects...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Subject Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Subject' : 'Edit Subject'}
        </DialogTitle>
        <DialogContent>
          <Box className='space-y-4 pt-2'>
            <TextField
              fullWidth
              label='Subject Name'
              value={newSubject.subjectName}
              onChange={(e) =>
                setNewSubject({ ...newSubject, subjectName: e.target.value })
              }
              placeholder='Enter subject name'
              required
              disabled={loading}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions className='border flex flex-wrap gap-y-2'>
          <Button
            variant='outlined'
            onClick={closeDialog}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={
              dialogMode === 'add' ? handleAddSubject : handleUpdateSubject
            }
            variant='contained'
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={loading || !newSubject.subjectName.trim()}
          >
            {loading
              ? 'Processing...'
              : dialogMode === 'add'
                ? 'Add Subject'
                : 'Update Subject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={dialog.open}
        onClose={closeConfirmDialog}
        onConfirm={dialog.onConfirm}
        title={dialog.title}
        message={dialog.message}
        severity={dialog.severity}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        loading={loading}
      />
      {/* Toast Notification */}
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </div>
  );
};

export default Subjects;
